import {
  UNDECLARED_UNKNOWN,
  type EngineCoverageBucket,
  type EnginePlanReport,
  type EngineTaskReport,
} from "@/services/migrations/engine/types";

/**
 * Presentation logic for engine coverage, kept out of the components so the one rule
 * that matters can be tested directly.
 *
 * THE RULE: when `undeclared > 0` the engine dropped something and recorded no reason
 * for it. Nobody can act on what they cannot see, so a screen that reports silent loss
 * must be INCAPABLE of also looking clean — no green, no primary CTA, no "validated"
 * checkmark. `successSuppressed` is that switch, and every success affordance on the
 * engine surface passes its tone through `suppressedTone` before rendering.
 *
 * COUNTS COME FROM THE SERVER, NOT FROM `list.length`. `/report` paginates its buckets
 * (`carried` alone is 838 KB of a real 876 KB report), so the length of a list in a
 * report payload is the length of a PAGE. The engine returns `undeclared_count` beside
 * `undeclared` for exactly this reason, and reading the list instead would under-report
 * silent loss on precisely the plans big enough to page.
 *
 * AND ABSENCE FAILS CLOSED. A count that never arrived is not zero. `undeclared_count`
 * missing means we cannot say nothing was lost — so we do not say it, and success stays
 * suppressed until a real zero shows up.
 *
 * Tones are the same four the rest of the app uses; no new colours are introduced.
 */
export type CoverageTone = "success" | "warm" | "destructive" | "neutral";

export interface CoverageBucketRow {
  bucket: EngineCoverageBucket;
  label: string;
  count: number;
  /** `false` when the server sent no count for this bucket. Never rendered as a number. */
  known: boolean;
  tone: CoverageTone;
  /** One sentence saying what being in this bucket means for the reader. */
  detail: string;
}

export interface CoverageVerdict {
  /** All six buckets, always — `undeclared` included at zero, deliberately. */
  buckets: CoverageBucketRow[];
  carried: number;
  total: number;
  carriedFraction: number;
  undeclared: number;
  /** `false` when the undeclared count never arrived. Treated as loss, not as zero. */
  undeclaredKnown: boolean;
  /** The engine lost something and did not say what — or will not say that it didn't. */
  silentLoss: boolean;
  /** No success affordance may render anywhere on this screen while true. */
  successSuppressed: boolean;
}

const BUCKET_LABELS: Record<EngineCoverageBucket, string> = {
  carried: "Carried",
  weakened: "Weakened",
  dropped_with_reason: "Dropped with a reason",
  unrepresentable: "Unrepresentable",
  rejected: "Rejected",
  undeclared: "Undeclared",
};

const BUCKET_DETAILS: Record<EngineCoverageBucket, string> = {
  carried: "The generated artifact grants these at least as strongly as the source did.",
  // Neither a drop nor a clean carry, and the only bucket where the behaviour survives in
  // a form the source would not recognise. Nothing in the coverage FRACTION shows it: a
  // weakened effect is not carried, so it is not in the numerator, and reading a fraction
  // alone tells you something is missing but not that it is still half-there.
  weakened:
    "Performed, but with less strength than the source demanded — the target grants a subset of what the source granted. Not a drop and not clean; each one names exactly which grants went missing.",
  dropped_with_reason:
    "The org's own describe says these cannot be carried. Each one names a manual step.",
  unrepresentable:
    "The target metadata type has nowhere to put these. Structural — no org change fixes it.",
  rejected:
    "Something declared a reason, but not one that excuses this kind of effect. A gate failure, with a manual step attached.",
  undeclared:
    "Dropped with NO declared reason. Nothing tells you what was lost or how to get it back.",
};

/** What a REPORTED zero in this bucket means, which is genuinely good news. */
const WEAKENED_CLEAR =
  "Nothing was carried at reduced strength — every effect the target performs, it performs in full.";

/**
 * What an ABSENT weakened count means. Not good news and not bad news, and above all not
 * a zero: the report does not currently carry this count even though the bucket pages, and
 * printing `0 weakened` would manufacture the reassurance the honest answer withholds.
 */
const WEAKENED_UNREPORTED =
  "The report did not carry a count for this bucket. Absent is not zero — open it to page whatever the engine has.";

/** What `undeclared` at zero means, which is genuinely good news and worth stating. */
const UNDECLARED_CLEAR = "Nothing was dropped without a reason.";

/** What an ABSENT undeclared count means, which is not good news and not bad news. */
const UNDECLARED_UNREPORTED =
  "The engine did not report this count. Absent is not zero — nothing here can be read as a clean result.";

/**
 * Resolve one server-sent count, distinguishing "zero" from "never told us".
 *
 * The list is used only as a LOWER BOUND when the count is missing: it may be a page,
 * so it can under-report, and it is reported as not-known so nothing downstream reads
 * it as a measurement.
 */
export function resolveCount(
  count: number,
  fallbackList: readonly unknown[]
): { count: number; known: boolean } {
  if (count !== UNDECLARED_UNKNOWN && count >= 0) return { count, known: true };
  return { count: fallbackList.length, known: false };
}

export function coverageVerdict(task: EngineTaskReport): CoverageVerdict {
  const undeclared = resolveCount(task.undeclared_count, task.undeclared);
  const dropped = resolveCount(task.dropped_count, task.dropped_with_reason);
  const unrepresentable = resolveCount(task.unrepresentable_count, task.unrepresentable);
  const rejected = resolveCount(task.rejected_count, task.rejected);
  const weakened = resolveCount(task.weakened_count, task.weakened);

  // FAIL CLOSED. `> 0` is loss; `!known` is the refusal to claim there was none.
  const silentLoss = !undeclared.known || undeclared.count > 0;

  return {
    buckets: [
      row("carried", task.carried_count, true, silentLoss ? "warm" : "success"),
      // WARM ABOVE ZERO AND WARM WHEN UNKNOWN, never success and never destructive. It is
      // not silent loss — every entry carries `CONTRACT_EFFECT_WEAKENED` and a reason
      // naming the missing grants — so it does not suppress success plan-wide; but it is
      // behaviour the customer's org will perform differently from the source, so it can
      // never wear the tone that says "this was fine". Neutral only at a REPORTED zero.
      {
        bucket: "weakened",
        label: BUCKET_LABELS.weakened,
        count: weakened.count,
        known: weakened.known,
        tone: !weakened.known || weakened.count > 0 ? "warm" : "neutral",
        detail: !weakened.known
          ? WEAKENED_UNREPORTED
          : weakened.count > 0
            ? BUCKET_DETAILS.weakened
            : WEAKENED_CLEAR,
      },
      row("dropped_with_reason", dropped.count, dropped.known, "warm"),
      row("unrepresentable", unrepresentable.count, unrepresentable.known, "warm"),
      // A declared reason that does not excuse. Destructive above zero because the
      // parity gate failed on it — but it is NOT silent loss (every entry names a code
      // and a manual step), so it does not suppress success plan-wide.
      row("rejected", rejected.count, rejected.known, rejected.count > 0 ? "destructive" : "warm"),
      // Always rendered, at every count. Destructive the moment it is non-zero OR
      // unknown; neutral (never green) at a reported zero, because a reassuring green
      // here would compete with the numbers that actually carry the verdict.
      {
        bucket: "undeclared",
        label: BUCKET_LABELS.undeclared,
        count: undeclared.count,
        known: undeclared.known,
        tone: silentLoss ? "destructive" : "neutral",
        detail: !undeclared.known
          ? UNDECLARED_UNREPORTED
          : undeclared.count > 0
            ? BUCKET_DETAILS.undeclared
            : UNDECLARED_CLEAR,
      },
    ],
    carried: task.carried_count,
    total: task.total_effects,
    carriedFraction: task.carried_fraction,
    undeclared: undeclared.count,
    undeclaredKnown: undeclared.known,
    silentLoss,
    successSuppressed: silentLoss,
  };
}

function row(
  bucket: EngineCoverageBucket,
  count: number,
  known: boolean,
  tone: CoverageTone
): CoverageBucketRow {
  return {
    bucket,
    label: BUCKET_LABELS[bucket],
    count,
    known,
    tone,
    detail: BUCKET_DETAILS[bucket],
  };
}

/**
 * Silent loss anywhere in a plan suppresses success everywhere in that plan's report.
 *
 * The plan total is the authority — it is summed server-side over EVERY task, and a
 * payload may hold a subset. A missing total is loss, for the same reason a missing
 * per-task count is: we cannot prove nothing was lost, so we do not claim it.
 */
export function planSilentLoss(report: EnginePlanReport): boolean {
  const total = report.undeclared_total;
  if (total == null || total === UNDECLARED_UNKNOWN) return true;
  if (total > 0) return true;
  return report.tasks.some((task) => coverageVerdict(task).silentLoss);
}

export function planSuccessSuppressed(report: EnginePlanReport): boolean {
  return planSilentLoss(report);
}

/**
 * The single choke point every success affordance passes through. Green becomes the
 * caution token while silent loss is on screen; nothing else is touched, so a failure
 * still reads as a failure and a caution still reads as a caution.
 */
export function suppressedTone(tone: CoverageTone, suppressed: boolean): CoverageTone {
  return suppressed && tone === "success" ? "warm" : tone;
}

/** Tone → the existing studio Badge variant. No new colours are introduced. */
export function toneBadgeVariant(
  tone: CoverageTone
): "success" | "warm" | "destructive" | "secondary" {
  return tone === "neutral" ? "secondary" : tone;
}

/** `0.9411` → `94.1%`. `total_effects === 0` has no fraction worth printing. */
export function carriedFractionLabel(fraction: number, total: number): string {
  if (!total) return "no effects";
  if (!Number.isFinite(fraction)) return "—";
  return `${(fraction * 100).toFixed(1)}%`;
}

/** A count that may not have arrived. `?` rather than a number nobody measured. */
export function countLabel(count: number, known: boolean): string {
  return known ? String(count) : "?";
}

/**
 * A coverage fraction is only as strong as the source it was measured against. A
 * partial source read or an unanswered grounding arm inflates it, and saying so beside
 * the number is the difference between a measurement and a boast.
 */
export function coverageCaveats(task: EngineTaskReport): string[] {
  const caveats: string[] = [];
  if (!task.source_complete) {
    caveats.push(
      "The source could not be read in full — this fraction is measured against a partial read."
    );
  }
  if (task.menus_unavailable.length > 0) {
    caveats.push(
      `${task.menus_unavailable.length} grounding arm(s) did not answer (${task.menus_unavailable.join(", ")}) — coverage above is inflated by exactly that much.`
    );
  }
  if (task.source_unreadable.length > 0) {
    caveats.push(`Unreadable source surfaces: ${task.source_unreadable.join(", ")}.`);
  }
  if (task.source_unverified.length > 0) {
    caveats.push(`Unverified source surfaces: ${task.source_unverified.join(", ")}.`);
  }
  return caveats;
}

/**
 * How a screen should describe an undeclared total. `UNDECLARED_UNKNOWN` means the field
 * never arrived, which is reported as unknown rather than smoothed into a clean zero.
 *
 * Takes the NUMBER, not a plan row: `GET /plans` carries no coverage at all, so there is
 * no plan-summary shape this could read one from. The verdict lives in `/report`.
 */
export function undeclaredSummary(undeclaredTotal: number | null | undefined): {
  tone: CoverageTone;
  label: string;
  known: boolean;
} {
  if (undeclaredTotal == null || undeclaredTotal === UNDECLARED_UNKNOWN) {
    return { tone: "warm", label: "undeclared not reported", known: false };
  }
  if (undeclaredTotal > 0) {
    return { tone: "destructive", label: `${undeclaredTotal} undeclared`, known: true };
  }
  return { tone: "neutral", label: "0 undeclared", known: true };
}
